import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import { get } from '../../lib/utils/axiosFetcher';
import { withLmaSafeApiLogging } from '../../lib/utils/lmaSafeLogging';

/**
 * Fulfilled action types for tab-specific dropdown thunks (see referrals/nonReferrals/processedReferrals slices).
 * Kept as string literals so dropdownSlice does not import those slices — avoids circular module graphs
 * (e.g. dropdownSlice → referralsSlice → … → dropdownApi → dropdownSlice) where thunks are undefined at init.
 */
const TAB_DROPDOWN_FULFILLED = new Set<string>([
  'referrals/fetchDropdownData/fulfilled',
  'nonReferrals/fetchDropdownData/fulfilled',
  'processedReferrals/fetchDropdownData/fulfilled',
]);

/**
 * API Response from LMA/GetLMAFilterDropdownValues
 */
export interface LMADropdownResponse {
  cities?: string[];
  states?: string[];
  patientReferralStatuses?: string[];
  ethnicity?: string[];
  associatedProtocols?: Array<{
    protocol: string;
    sponsorName: string;
    studyName: string;
    nctid: string;
    sites: Array<{
      code: string;
      name: string;
      city: string;
      state: string;
    }>;
  }>;
  qualifiedProtocols?: string[];
  lmaUsers?: Array<{
    userId: string;
    userName: string;
  }>;
}

export interface DropdownState {
  data: LMADropdownResponse | null;
  loading: boolean;
  error: string | null;
  lastUpdated: string | null;
}

const initialState: DropdownState = {
  data: null,
  loading: false,
  error: null,
  lastUpdated: null,
};

// Async thunk for fetching dropdown data (called once on page load)
export const fetchDropdownData = createAsyncThunk(
  'dropdown/fetchDropdownData',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await withLmaSafeApiLogging(
        {
          operation: 'fetchDropdownData',
          method: 'GET',
          endpoint: '/LMA/GetLMAFilterDropdownValues',
        },
        () => get('/LMA/GetLMAFilterDropdownValues')
      );
      return response?.data as LMADropdownResponse;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        'Unable to load filter options. Please reload the page or contact IT support if the issue persists.';
      dispatch({
        type: 'toast/showToast',
        payload: { message: errorMessage, severity: 'error' },
      });
      return rejectWithValue(errorMessage);
    }
  }
);

const dropdownSlice = createSlice({
  name: 'dropdown',
  initialState,
  reducers: {
    clearDropdownData: (state) => {
      state.data = null;
      state.error = null;
      state.lastUpdated = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDropdownData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDropdownData.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchDropdownData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Mirror tab-specific dropdown payloads into shared lmaDropdown (no slice imports — see TAB_DROPDOWN_FULFILLED)
      .addMatcher(
        (action): action is PayloadAction<LMADropdownResponse> =>
          typeof action.type === 'string' && TAB_DROPDOWN_FULFILLED.has(action.type),
        (state, action) => {
          state.data = action.payload;
          state.lastUpdated = new Date().toISOString();
        }
      );
  },
});

export const { clearDropdownData } = dropdownSlice.actions;
export default dropdownSlice.reducer;
